// Shared helpers for rendering the deterministic ClinSpark study package.
export type Row = Record<string, any>

export const NOT_SPECIFIED = 'NOT SPECIFIED'

export const rows = (value: unknown): Row[] =>
  Array.isArray(value) ? value.filter((item): item is Row => Boolean(item && typeof item === 'object')) : []

export const value = (item: unknown): string => {
  if (item === null || item === undefined || item === '' || item === NOT_SPECIFIED) return '-'
  if (Array.isArray(item)) return item.map(value).join(', ')
  if (typeof item === 'object') return JSON.stringify(item)
  return String(item)
}

export const searched = (item: Row, query: string) =>
  !query.trim() || JSON.stringify(item).toLowerCase().includes(query.trim().toLowerCase())

export const relationKey = (item: unknown): string => String(item ?? '').trim().replace(/\s+/g, ' ').toLowerCase()

export const known = (item: unknown): boolean =>
  Boolean(item && !['not specified', 'unknown', '-'].includes(relationKey(item)))

export const displayLabel = (item: unknown): string => {
  const label = value(item)
  return label === '-' ? label : label[0].toUpperCase() + label.slice(1)
}

export const sameKnown = (a: unknown, b: unknown): boolean => known(a) && known(b) && relationKey(a) === relationKey(b)

export function criterionPrefix(type: unknown): 'INC' | 'EXC' {
  return String(type || '').toLowerCase().startsWith('ex') ? 'EXC' : 'INC'
}

export function criterionText(item: Row): string {
  return String(item.criterion_text || item.text || item.criterion || item.description || '').trim()
}

/** Parse a blueprint note line such as "EXC #17: Have currently ..." */
export function parseCriterionNote(line: string): { prefix: 'INC' | 'EXC'; number: string; text: string } | null {
  const match = line.match(/^\s*(INC|EXC)\s*#\s*([^:]+?)\s*:\s*([\s\S]*)$/i)
  if (!match) return null
  return { prefix: match[1].toUpperCase() as 'INC' | 'EXC', number: match[2].trim(), text: match[3].trim() }
}

export function criterionKey(prefix: string, number: unknown): string {
  return `${prefix.toUpperCase()}#${String(number ?? '').trim().toLowerCase()}`
}

export function downloadText(filename: string, content: string, mime = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // Revoke after the click has been processed by the browser.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** RFC 4180 CSV with formula-injection protection for spreadsheet tools. */
export function toCsv(header: string[], data: unknown[][]): string {
  const escapeCell = (cell: unknown) => {
    let text = cell === null || cell === undefined ? '' : String(cell)
    if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  return [header, ...data].map((line) => line.map(escapeCell).join(',')).join('\r\n')
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Fall through to the legacy path (e.g. insecure context).
  }
  try {
    const area = document.createElement('textarea')
    area.value = text
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    const ok = document.execCommand('copy')
    area.remove()
    return ok
  } catch {
    return false
  }
}

/** Case-insensitive highlight of a search query inside text. */
export function splitHighlight(text: string, query: string): Array<{ text: string; match: boolean }> {
  const needle = query.trim()
  if (!needle) return [{ text, match: false }]
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'ig'))
  return parts.filter((part) => part !== '').map((part) => ({ text: part, match: part.toLowerCase() === needle.toLowerCase() }))
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count.toLocaleString()} ${count === 1 ? singular : plural}`
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '-'
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB']
  let size = bytes / 1024
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unit]}`
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/** Document roles a protocol PDF can play in a study's source set. */
export const DOCUMENT_ROLES: ReadonlyArray<readonly [string, string]> = [
  ['unknown', 'Not specified'],
  ['master', 'Master protocol'],
  ['intervention', 'Intervention-specific (ISA)'],
  ['screening', 'Screening protocol'],
  ['dosing', 'Dosing / treatment protocol'],
  ['extension', 'Extension protocol'],
  ['amendment', 'Amendment'],
  ['other', 'Other'],
]

export function roleLabel(role: unknown): string {
  const key = String(role || 'unknown').toLowerCase()
  const known = DOCUMENT_ROLES.find(([value]) => value === key)
  if (known) return known[1]
  return key.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

/** Short label for a source document chip ("Master", "Screening", or the file name). */
export function sourceShortLabel(source: { document_role?: string; filename?: string } | null | undefined): string {
  const role = String(source?.document_role || '').toLowerCase()
  if (role && role !== 'unknown') return roleLabel(role).replace(/\s*\(.*\)$/, '').replace(/ protocol$/i, '')
  return String(source?.filename || 'Source').replace(/\.pdf$/i, '')
}

export type CriterionRecord = {
  key: string
  prefix: 'INC' | 'EXC'
  number: string
  text: string
  formatted: string
  population: string | null
  sourcePage: unknown
  raw: Row
}

/** Index of package criteria by "INC#6" style keys (also by original number). */
export function buildCriterionIndex(eligibility: { inclusion?: unknown; exclusion?: unknown } | undefined | null): Map<string, CriterionRecord> {
  const index = new Map<string, CriterionRecord>()
  for (const [type, items] of [['inclusion', eligibility?.inclusion], ['exclusion', eligibility?.exclusion]] as const) {
    const prefix = type === 'inclusion' ? 'INC' : 'EXC'
    rows(items).forEach((item, position) => {
      const number = String(item.criterion_number ?? item.id ?? item.criterion_id ?? position + 1)
      const record: CriterionRecord = {
        key: criterionKey(prefix, number),
        prefix,
        number,
        text: criterionText(item),
        formatted: String(item.criterion_text_formatted || '').trim() || criterionText(item),
        population: known(item.population) ? String(item.population) : null,
        sourcePage: item.source_page,
        raw: item,
      }
      index.set(record.key, record)
      // Merged sources may qualify numbers ("3 (screening)"); keep the original
      // number resolvable when it is unambiguous.
      const original = item.source_criterion_number
      if (original !== undefined && original !== null) {
        const alias = criterionKey(prefix, original)
        if (!index.has(alias)) index.set(alias, record)
      }
    })
  }
  return index
}

/** Strip the "..." that build notes append when they truncate long criteria. */
export function stripTruncation(text: string): string {
  return text.replace(/\s*(?:\.{3}|…)\s*$/, '').trim()
}

export function anchorId(...parts: unknown[]): string {
  return parts.map((part) => String(part ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')).filter(Boolean).join('-')
}

/** Scroll an element into view where supported (jsdom has no layout APIs). */
export function scrollToElement(element: Element | null | undefined, block: ScrollLogicalPosition = 'start') {
  if (element && typeof (element as HTMLElement).scrollIntoView === 'function') {
    const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ;(element as HTMLElement).scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block })
  }
}

const SCOPE_LABELS: Record<string, string> = {
  FULL_PROTOCOL: 'Full protocol',
  SCREENING_ONLY_PROTOCOL: 'Screening only',
  INTERVENTION_SPECIFIC_PROTOCOL: 'Intervention-specific',
  MASTER_OR_PARENT_PROTOCOL: 'Master / parent protocol',
  MULTI_DOCUMENT_STUDY_SOURCE_SET: 'Multi-document source set',
}

const ACRONYMS = new Set(['pk', 'pd', 'ae', 'sae', 'ecg', 'soa', 'ie', 'ip', 'eot', 'eos'])

export function humanizeCode(text: unknown): string {
  return String(text ?? '')
    .replace(/_/g, ' ')
    .replace(/\bor\b/gi, '/')
    .replace(/\b[a-z]+\b/gi, (word) => (ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : word.toLowerCase()))
    .replace(/^\w/, (c) => c.toUpperCase())
}

export function scopeLabel(scope: unknown): string {
  const key = String(scope ?? '')
  return SCOPE_LABELS[key] || humanizeCode(key)
}
